import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  setAuthTokenGetter,
  setRefreshTokenGetter,
  setUpdateTokensCallback,
  setLogoutCallback,
  setSubscriptionExpiredCallback,
  setOrgDisclaimerRequiredCallback,
  authApi,
  subscriptionApi,
  questionnaireApi,
  settingsApi,
} from "@/lib/api";
import {
  OnboardingCompleteRequestBody,
  OnboardingCompleteResponse,
  OnboardingStatus,
  SocialAuthProvider,
  SocialLinkingHint,
  SocialSessionToken,
  Token,
  UserAccessDecision,
  UserProfileResponse,
} from "@/features/auth/types";
import {
  SubscriptionStatusResponse,
  ApiAnswer,
  SettingsResponse,
  ApiError,
} from "@/types";
import { useToast } from "@/contexts/ToastContext";
import {
  touchPresence,
  writePresence,
  cancelPendingRetry,
  VISIT_TICK_MS,
} from "@/lib/visitTracker";
import {
  determineResumeStepFromAnswers,
  isQuestionnaireCompleteFromAnswers,
  canAccessChatFromAccess,
  canUseAppFromAccess,
  hasAccessDecision,
  shouldBlockPersonalQuestionnaire,
  legacySubscriptionAllowsApp,
  legacySubscriptionRequiresPricing,
  mapLanguageFromApi,
  mapThemeFromApi,
  shouldShowAccessDeniedFromAccess,
  shouldShowPricingFromAccess,
  translateBackendMessage,
  isOrgDisclaimerRequired,
  isOrgDisclaimerRequiredError,
} from "@/helpers";
import { useTheme } from "@/contexts/ThemeContext";

interface AuthContextType {
  user: UserProfileResponse | null;
  accessDecision: UserAccessDecision | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  onboardingStatus: OnboardingStatus | null;
  subscriptionStatus: SubscriptionStatusResponse | null;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
    org?: string | null
  ) => Promise<{ shouldNavigateTo: string }>;
  logout: () => Promise<void>;
  signup: (userData: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    date_of_birth: string;
    terms_accepted: boolean;
    privacy_policy_accepted: boolean;
    language: string;
    theme: string;
  }) => Promise<{ shouldNavigateTo: string }>;
  updateUser: (updates: Partial<UserProfileResponse>) => void;
  getAccessToken: () => string | null;
  checkOnboardingStatus: () => Promise<OnboardingStatus | null>;
  checkSubscriptionStatus: () => Promise<SubscriptionStatusResponse | null>;
  refreshCurrentUserAccess: () => Promise<UserAccessDecision | null>;
  resendVerificationEmail: (email: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  loginWithSocial: (
    provider: SocialAuthProvider,
    providerToken: string,
    rememberMe?: boolean,
    org?: string | null
  ) => Promise<
    | { outcome: "session"; shouldNavigateTo: string }
    | { outcome: "linking_required"; linking: SocialLinkingHint }
  >;
  applyTokenResponseAfterSocial: (
    tokenResponse: SocialSessionToken,
    rememberMe?: boolean
  ) => Promise<{ shouldNavigateTo: string }>;
  loginWithEntra: (
    code: string,
    rememberMe?: boolean
  ) => Promise<{ shouldNavigateTo: string }>;
  refreshAfterOnboardingComplete: (
    body: OnboardingCompleteRequestBody
  ) => Promise<OnboardingCompleteResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

// Secure token storage keys
const ACCESS_TOKEN_KEY = "harvn_access_token";
const REFRESH_TOKEN_KEY = "harvn_refresh_token";
const REMEMBER_ME_KEY = "harvn_remember_me";
const REMEMBER_ME_EXPIRY_KEY = "harvn_remember_me_expiry";
const ACCOUNT_COMPLETION_HINT_KEY = "harvn_account_completion_required";

// Remember Me session duration (10 days)
const REMEMBER_ME_DURATION = 10 * 24 * 60 * 60 * 1000; // 10 days in milliseconds

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatus | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatusResponse | null>(null);
  const { showToast } = useToast();
  const { t, i18n } = useTranslation();
  const { setTheme } = useTheme();
  const navigate = useNavigate();

  // SECURITY: Tokens stored in memory first, then in appropriate storage
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  // Ref to prevent multiple auth status checks
  const isCheckingAuthRef = useRef(false);
  const userRef = useRef<UserProfileResponse | null>(null);
  const orgDisclaimerRefreshInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Helper function to securely store tokens with Remember Me support
  const storeTokens = (
    accessToken: string,
    refreshToken: string,
    rememberMe: boolean = false
  ) => {
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);

    // Always set session storage (tab-scoped)
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

    // Also set localStorage for cross-tab visibility
    // When rememberMe is true: persistent with expiry
    // When false: non-persistent session broadcast (cleared on logout)
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    if (rememberMe) {
      const expiryTime = Date.now() + REMEMBER_ME_DURATION;
      localStorage.setItem(REMEMBER_ME_KEY, "true");
      localStorage.setItem(REMEMBER_ME_EXPIRY_KEY, expiryTime.toString());
    } else {
      localStorage.setItem(REMEMBER_ME_KEY, "false");
      localStorage.removeItem(REMEMBER_ME_EXPIRY_KEY);
    }
  };

  // Helper function to check if Remember Me token is still valid
  const isRememberMeValid = (): boolean => {
    const isRemembered = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    const expiryTime = localStorage.getItem(REMEMBER_ME_EXPIRY_KEY);

    if (!isRemembered || !expiryTime) {
      return false;
    }

    const expiry = parseInt(expiryTime, 10);
    const now = Date.now();

    if (now > expiry) {
      // Token expired, clean up
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(REMEMBER_ME_EXPIRY_KEY);

      return false;
    }

    return true;
  };

  const setAccountCompletionHint = (required: boolean): void => {
    if (required) {
      sessionStorage.setItem(ACCOUNT_COMPLETION_HINT_KEY, "true");
      localStorage.setItem(ACCOUNT_COMPLETION_HINT_KEY, "true");
      return;
    }

    sessionStorage.removeItem(ACCOUNT_COMPLETION_HINT_KEY);
    localStorage.removeItem(ACCOUNT_COMPLETION_HINT_KEY);
  };

  const getAccountCompletionHint = (): boolean => {
    return (
      sessionStorage.getItem(ACCOUNT_COMPLETION_HINT_KEY) === "true" ||
      localStorage.getItem(ACCOUNT_COMPLETION_HINT_KEY) === "true"
    );
  };

  const tokenRequiresAccountCompletion = (
    tokenResponse: Token | SocialSessionToken
  ): boolean => {
    return (
      (tokenResponse as Partial<SocialSessionToken>).onboarding_required === true
    );
  };

  const isAccountCompletionRequiredError = (error: unknown): boolean => {
    const apiError = error as ApiError;
    const errorKey = apiError.backendData?.error_key;
    return (
      errorKey === "ERROR_ONBOARDING_INCOMPLETE" ||
      errorKey === "ERROR_ACCOUNT_COMPLETION_REQUIRED"
    );
  };

  const markAccountCompletionAsRequired = (): void => {
    setAccountCompletionHint(true);
    setUser((previousUser) => {
      if (!previousUser) {
        return previousUser;
      }

      return {
        ...previousUser,
        user: { ...previousUser.user, account_completion_required: true },
      };
    });
    setOnboardingStatus(null);
    setSubscriptionStatus(null);
  };

  const resolveAccountCompletionRequired = (
    userData: UserProfileResponse,
    tokenHintRequired: boolean = false
  ): boolean => {
    const backendFlag = userData.user.account_completion_required;

    if (backendFlag === true) {
      return true;
    }

    if (backendFlag === false) {
      return false;
    }

    if (tokenHintRequired) {
      return true;
    }

    return getAccountCompletionHint();
  };

  // Helper function to get token from storage (checks both localStorage and sessionStorage)
  const getStoredToken = (): string | null => {
    if (accessToken) return accessToken;

    // 1) Check session storage first (tab-scoped)
    const sessionToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (sessionToken) {
      setAccessToken(sessionToken);
      return sessionToken;
    }

    // 2) Check localStorage for persistent Remember Me token
    if (isRememberMeValid()) {
      const persistentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (persistentToken) {
        setAccessToken(persistentToken);
        sessionStorage.setItem(ACCESS_TOKEN_KEY, persistentToken);
        return persistentToken;
      }
    }

    // 3) As cross-tab session propagation, adopt localStorage token even if not Remember Me
    const localToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (localToken) {
      setAccessToken(localToken);
      sessionStorage.setItem(ACCESS_TOKEN_KEY, localToken);
      return localToken;
    }

    return null;
  };

  // Helper function to clear all token storage
  const clearToken = () => {
    setAccessToken(null);
    setRefreshToken(null);

    // Clear both storage types
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    localStorage.removeItem(REMEMBER_ME_EXPIRY_KEY);
    setAccountCompletionHint(false);

    // Broadcast logout to other tabs by toggling a nonce value
    try {
      localStorage.setItem("harvn_auth_broadcast", Math.random().toString());
      localStorage.removeItem("harvn_auth_broadcast");
    } catch {}
  };

  // Helper function to get current access token
  const getAccessToken = () => getStoredToken();

  // Helper function to get refresh token from storage
  const getRefreshToken = (): string | null => {
    if (refreshToken) return refreshToken;

    // Check session storage first (tab-scoped)
    const sessionToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (sessionToken) {
      setRefreshToken(sessionToken);
      return sessionToken;
    }

    // Check localStorage for persistent Remember Me token
    if (isRememberMeValid()) {
      const persistentToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (persistentToken) {
        setRefreshToken(persistentToken);
        sessionStorage.setItem(REFRESH_TOKEN_KEY, persistentToken);
        return persistentToken;
      }
    }

    // As cross-tab session propagation, adopt localStorage token
    const localToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (localToken) {
      setRefreshToken(localToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, localToken);
      return localToken;
    }

    return null;
  };

  // Helper function to update tokens in memory (called from API layer during refresh)
  const updateTokensInMemory = (
    newAccessToken: string,
    newRefreshToken: string
  ) => {
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    // Update storage
    sessionStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
  };

  // Helper function to force logout (called from API layer when refresh fails)
  const forceLogout = () => {
    setUser(null);
    setOnboardingStatus(null);
    setSubscriptionStatus(null);
    clearToken();

    showToast({
      title: t("features.auth.session.expiredTitle"),
      description: t("features.auth.session.expiredDescription"),
      variant: "destructive",
    });
  };

  // Helper function to handle subscription/trial expired (called from API layer on 430)
  // Access-aware routing prevents organization denials from becoming pricing redirects.
  const handleOrgDisclaimerRequired = async () => {
    // Already gated: do not stack /users/me refreshes from 403 storms.
    if (userRef.current?.org_disclaimer?.required === true) {
      return;
    }
    if (orgDisclaimerRefreshInFlightRef.current) {
      await orgDisclaimerRefreshInFlightRef.current;
      return;
    }
    const refreshPromise = (async () => {
      try {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
      } catch {
        // Gate will appear once /users/me succeeds; do not treat as access-denied.
      }
    })();
    orgDisclaimerRefreshInFlightRef.current = refreshPromise;
    try {
      await refreshPromise;
    } finally {
      if (orgDisclaimerRefreshInFlightRef.current === refreshPromise) {
        orgDisclaimerRefreshInFlightRef.current = null;
      }
    }
  };

  const handleSubscriptionExpired = (error?: ApiError) => {
    const errorKey = error?.backendData?.error_key;
    if (errorKey === "ORG_DISCLAIMER_REQUIRED") {
      void handleOrgDisclaimerRequired();
      return;
    }
    if (errorKey === "ERROR_ORGANIZATION_INACTIVE") {
      navigate("/auth/access-denied", { replace: true });
      return;
    }

    const currentAccess = userRef.current?.access;
    if (hasAccessDecision(currentAccess)) {
      const canUseApp = canUseAppFromAccess(currentAccess);
      const canAccessChat = canAccessChatFromAccess(currentAccess);
      const shouldShowPricing = shouldShowPricingFromAccess(currentAccess);
      const shouldShowAccessDenied =
        shouldShowAccessDeniedFromAccess(currentAccess);

      if (canUseApp === true || canAccessChat === true) {
        return;
      }

      if (shouldShowPricing === true) {
        navigate("/pricing", { replace: true });
        return;
      }

      if (shouldShowAccessDenied === true || canUseApp === false) {
        navigate("/auth/access-denied", { replace: true });
        return;
      }
    }

    // Update subscription status to reflect expired state immediately
    // Setting access_reason to "freemium_expired" ensures logout() skips close-all-sessions
    setSubscriptionStatus((prev) =>
      prev
        ? { ...prev, subscription_status: "expired", can_access_chat: false, access_reason: "freemium_expired" }
        : prev
    );

    showToast({
      title: t("features.auth.subscriptionExpired.title"),
      description: t("features.auth.subscriptionExpired.description"),
      variant: "destructive",
    });

    navigate("/auth/subscription-expired", { replace: true });
  };

  // Web visit signal (SPEC-020), effect A: the user was just set (bootstrap,
  // reload, login, storage-driven re-bootstrap); only a visible tab counts.
  const visitUserId = user?.user?.id;
  useEffect(() => {
    if (visitUserId && document.visibilityState === "visible") {
      touchPresence(visitUserId, Date.now());
    }
  }, [visitUserId]);

  // Web visit signal (SPEC-020), effect B: presence triggers while a user is set.
  useEffect(() => {
    if (!visitUserId) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        touchPresence(visitUserId, Date.now());
      } else {
        writePresence(visitUserId, Date.now());
      }
    };
    const handlePageHide = () => writePresence(visitUserId, Date.now());
    const tick = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        touchPresence(visitUserId, Date.now());
      }
    }, VISIT_TICK_MS);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      window.clearInterval(tick);
      cancelPendingRetry();
    };
  }, [visitUserId]);

  useEffect(() => {
    // SECURITY: Connect secure token getters and callbacks to API layer
    setAuthTokenGetter(getAccessToken);
    setRefreshTokenGetter(getRefreshToken);
    setUpdateTokensCallback(updateTokensInMemory);
    setLogoutCallback(forceLogout);
    setSubscriptionExpiredCallback(handleSubscriptionExpired);
    setOrgDisclaimerRequiredCallback(handleOrgDisclaimerRequired);

    // Check for existing session on app load
    checkAuthStatus();

    // Cross-tab auth sync: listen for token changes
    const handleStorage = (e: StorageEvent) => {
      if (!e.key) return;
      // Re-check auth for token changes or auth broadcast pings
      if (
        e.key === ACCESS_TOKEN_KEY ||
        e.key === REMEMBER_ME_KEY ||
        e.key === REMEMBER_ME_EXPIRY_KEY ||
        e.key === "harvn_auth_broadcast"
      ) {
        checkAuthStatus();
      }
    };
    window.addEventListener("storage", handleStorage);
    // Ask other tabs for session token if we don't have one and Remember Me isn't active
    try {
      if (!sessionStorage.getItem(ACCESS_TOKEN_KEY)) {
        localStorage.setItem("harvn_auth_broadcast", Math.random().toString());
        localStorage.removeItem("harvn_auth_broadcast");
      }
    } catch {}
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const checkAuthStatus = async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuthRef.current) {
      return;
    }

    try {
      isCheckingAuthRef.current = true;
      const token = getStoredToken();
      if (token) {
        // Try to get current user data
        const userData = await authApi.getCurrentUser();
        const requiresAccountCompletion =
          resolveAccountCompletionRequired(userData);
        const nextUserData = requiresAccountCompletion
          ? {
              ...userData,
              user: { ...userData.user, account_completion_required: true },
            }
          : userData;
        setUser(nextUserData);

        // Fetch and apply user settings (language and theme) for existing sessions
        await fetchAndApplyUserSettings();

        if (requiresAccountCompletion) {
          setAccountCompletionHint(true);
          setOnboardingStatus(null);
          setSubscriptionStatus(null);
          return;
        }

        setAccountCompletionHint(false);

        // Disclaimer gate owns the session until accept — skip blocked status APIs.
        if (isOrgDisclaimerRequired(nextUserData.org_disclaimer)) {
          setOnboardingStatus(null);
          setSubscriptionStatus(null);
          return;
        }

        // Get onboarding status for routing with fallback
        try {
          const status = await authApi.getOnboardingStatus();
          setOnboardingStatus(status);
        } catch (onboardingError) {
          if (isAccountCompletionRequiredError(onboardingError)) {
            markAccountCompletionAsRequired();
            return;
          }
          if (!isOrgDisclaimerRequiredError(onboardingError)) {
            console.warn(
              t("common.errors.failedToGetOnboardingStatus"),
              onboardingError
            );
          }
          // If onboarding status API fails, default to null (safe fallback)
          setOnboardingStatus(null);
        }

        // Get subscription status for routing with fallback
        try {
          const response = await subscriptionApi.getStatus();
          const status = response as unknown as SubscriptionStatusResponse;
          setSubscriptionStatus(status);
        } catch (subscriptionError) {
          if (isAccountCompletionRequiredError(subscriptionError)) {
            markAccountCompletionAsRequired();
            return;
          }
          if (!isOrgDisclaimerRequiredError(subscriptionError)) {
            console.warn(
              t("common.errors.failedToGetSubscriptionStatus"),
              subscriptionError
            );
          }
          // If subscription status API fails, default to null (safe fallback)
          setSubscriptionStatus(null);
        }
      } else {
        // No token found - user is not authenticated
        // Set subscriptionStatus to a default value to prevent infinite loading
        setUser(null);
        setOnboardingStatus(null);
        setSubscriptionStatus(null);
      }
    } catch (error) {
      // Clear invalid token
      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);
    } finally {
      setIsLoading(false);
      isCheckingAuthRef.current = false;
    }
  };

  const checkOnboardingStatus = async (): Promise<OnboardingStatus | null> => {
    try {
      const status = await authApi.getOnboardingStatus();
      setOnboardingStatus(status);
      return status;
    } catch (error) {
      return null;
    }
  };

  const checkSubscriptionStatus =
    async (): Promise<SubscriptionStatusResponse | null> => {
      if (isOrgDisclaimerRequired(userRef.current?.org_disclaimer)) {
        return null;
      }
      try {
        const response = await subscriptionApi.getStatus();
        const status = response as unknown as SubscriptionStatusResponse;
        setSubscriptionStatus(status);
        return status;
      } catch (error) {
        if (isAccountCompletionRequiredError(error)) {
          markAccountCompletionAsRequired();
          return null;
        }
        if (isOrgDisclaimerRequiredError(error)) {
          return null;
        }
        const apiError = error as ApiError;
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.errors.failedToGetSubscriptionStatus");
        showToast({
          description: errorMessage,
          variant: "destructive",
        });
        return null;
      }
    };

  const refreshCurrentUserAccess =
    async (): Promise<UserAccessDecision | null> => {
      try {
        const userData: UserProfileResponse = await authApi.getCurrentUser();
        setUser(userData);
        return userData.access ?? null;
      } catch (error) {
        return null;
      }
    };

  // Helper function to check questionnaire progress and determine resume step
  const checkQuestionnaireProgress = async (): Promise<{
    isComplete: boolean;
    resumeStep?: number;
  }> => {
    try {
      // Get user's questionnaire data
      const answers: ApiAnswer[] = await questionnaireApi.getMyAnswers();

      // Check if questionnaire is complete
      const isComplete = isQuestionnaireCompleteFromAnswers(answers);

      if (isComplete) {
        return { isComplete: true };
      }

      // If not complete, determine which step to resume from
      const resumeStep = determineResumeStepFromAnswers(answers);
      return { isComplete: false, resumeStep };
    } catch (error) {
      // Get translated error message from backend
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("common.errors.failedToCheckQuestionnaireProgress");

      showToast({
        variant: "destructive",
        title: t("common.error"),
        description: errorMessage,
      });

      // If API fails, assume questionnaire needs to be started from beginning
      return { isComplete: false, resumeStep: 1 };
    }
  };

  // Fetch and apply user settings (language and theme)
  const fetchAndApplyUserSettings = async (): Promise<void> => {
    try {
      const settings = (await settingsApi.getMySettings()) as SettingsResponse;

      // Apply language setting if available
      if (settings.language) {
        const frontendLanguage = mapLanguageFromApi(settings.language);

        // Only change language if it's different from current
        if (i18n.language !== frontendLanguage) {
          await i18n.changeLanguage(frontendLanguage);
        }
      }

      // Apply theme setting if available
      if (settings.theme) {
        const frontendTheme = mapThemeFromApi(settings.theme);

        setTheme(frontendTheme);
      }
    } catch (error) {
      // Log the translated error message
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("common.errors.failedToFetchUserSettings");

      console.warn(errorMessage);
      // Continue with login flow even if settings fail
    }
  };

  const determineOnboardingDestination = async (
    onboardingData: OnboardingStatus | null,
    access?: UserAccessDecision | null
  ): Promise<string> => {
    const isOnboardingCompleted = onboardingData?.onboardingCompleted === true;

    if (isOnboardingCompleted) {
      // Onboarding fully completed, go to chat
      return "/chat";
    }

    // Organisation-entitled members never get the personal questionnaire,
    // so skip the progress probe entirely.
    if (shouldBlockPersonalQuestionnaire(access)) {
      return "/chat";
    }

    // Onboarding not completed - check questionnaire progress for returning users
    try {
      const questionnaireProgress = await checkQuestionnaireProgress();

      if (questionnaireProgress.isComplete) {
        return "/chat";
      }

      // Questionnaire not complete - resume at the appropriate step
      if (
        questionnaireProgress.resumeStep &&
        questionnaireProgress.resumeStep > 1
      ) {
        // User has some progress, resume at the specific step
        return `/onboarding/questionnaire?step=${questionnaireProgress.resumeStep}`;
      }

      // No progress or starting from step 1, show questionnaire invitation
      return "/chat";
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("common.errors.failedToCheckQuestionnaireProgress");
      showToast({
        description: errorMessage,
        variant: "destructive",
      });
      // Fallback to questionnaire invitation if progress check fails
      return "/chat";
    }
  };

  const determineLoginDestination = async (
    userData: UserProfileResponse,
    onboardingData: OnboardingStatus | null,
    subscriptionData: SubscriptionStatusResponse | null
  ): Promise<string> => {
    const access = userData.access;

    if (hasAccessDecision(access)) {
      if (shouldShowPricingFromAccess(access) === true) {
        return "/pricing";
      }

      if (shouldShowAccessDeniedFromAccess(access) === true) {
        return "/auth/access-denied";
      }

      const canUseApp = canUseAppFromAccess(access);
      if (canUseApp === true) {
        return determineOnboardingDestination(onboardingData, access);
      }

      if (canUseApp === false) {
        return "/auth/access-denied";
      }
    }

    // Legacy fallback when /users/me.access is unavailable.
    const isSubscriptionExpired =
      subscriptionData?.subscription_status === "expired" ||
      subscriptionData?.subscription_status === "past_due" ||
      (subscriptionData?.subscription_status === "freemium" &&
        subscriptionData?.subscription_trial_remaining === 0);

    if (isSubscriptionExpired) {
      // User needs to renew subscription - route to subscription expired screen
      return "/auth/subscription-expired";
    }

    if (
      legacySubscriptionRequiresPricing(subscriptionData) ||
      !legacySubscriptionAllowsApp(subscriptionData)
    ) {
      // User needs to select a subscription plan first (null subscriptionData = uninitialized)
      return "/pricing";
    }

    return determineOnboardingDestination(onboardingData, access);
  };

  const handleLoginFlowError = (error: ApiError) => {
    let errorMessage = t("features.auth.login.errors.fallbackError");
    let errorTitle = t("features.auth.login.errors.generalError");

    if (error?.backendData?.error_key) {
      errorMessage = translateBackendMessage(
        error.backendData,
        t,
        error.message || t("features.auth.login.errors.fallbackError")
      );

      const errorKey = error.backendData.error_key;
      if (errorKey.includes("LOCKED") || errorKey.includes("TOO_MANY")) {
        errorTitle = t("features.auth.login.errors.accountLocked");
      }
    } else {
      errorMessage =
        error.message || t("features.auth.login.errors.fallbackError");
    }

    showToast({
      title: errorTitle,
      description: errorMessage,
      variant: "destructive",
      duration: 8000,
    });
  };

  const completeSessionAfterTokenResponse = async (
    tokenResponse: Token | SocialSessionToken,
    rememberMe: boolean
  ): Promise<{ shouldNavigateTo: string }> => {
    const refreshTokenValue =
      tokenResponse.refresh_token || tokenResponse.access_token;
    storeTokens(tokenResponse.access_token, refreshTokenValue, rememberMe);

    const userData: UserProfileResponse = await authApi.getCurrentUser();
    const requiresAccountCompletion =
      resolveAccountCompletionRequired(
        userData,
        tokenRequiresAccountCompletion(tokenResponse)
      );
    const nextUserData = requiresAccountCompletion
      ? {
          ...userData,
          user: { ...userData.user, account_completion_required: true },
        }
      : userData;
    setUser(nextUserData);

    if (requiresAccountCompletion) {
      setAccountCompletionHint(true);
      await fetchAndApplyUserSettings();
      return { shouldNavigateTo: "/auth/account/complete" };
    }

    setAccountCompletionHint(false);
    await fetchAndApplyUserSettings();

    // While disclaimer is required, onboarding/subscription endpoints 403 — skip them.
    if (isOrgDisclaimerRequired(userData.org_disclaimer)) {
      setOnboardingStatus(null);
      setSubscriptionStatus(null);
      const destination = await determineLoginDestination(
        userData,
        null,
        null
      );
      return { shouldNavigateTo: destination };
    }

    let onboardingData: OnboardingStatus | null = null;
    try {
      onboardingData = await authApi.getOnboardingStatus();
      setOnboardingStatus(onboardingData);
    } catch (onboardingError) {
      if (isAccountCompletionRequiredError(onboardingError)) {
        markAccountCompletionAsRequired();
        return { shouldNavigateTo: "/auth/account/complete" };
      }
      if (isOrgDisclaimerRequiredError(onboardingError)) {
        setOnboardingStatus(null);
      } else {
        const apiError = onboardingError as ApiError;
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.errors.failedToGetOnboardingStatus");
        showToast({
          title: t("features.auth.login.errors.generalError"),
          description: errorMessage,
          variant: "destructive",
        });
        setOnboardingStatus(null);
      }
    }

    let subscriptionData: SubscriptionStatusResponse | null = null;
    try {
      const response = await subscriptionApi.getStatus();
      subscriptionData = response as unknown as SubscriptionStatusResponse;
      setSubscriptionStatus(subscriptionData);
    } catch (subscriptionError) {
      if (isAccountCompletionRequiredError(subscriptionError)) {
        markAccountCompletionAsRequired();
        return { shouldNavigateTo: "/auth/account/complete" };
      }
      if (isOrgDisclaimerRequiredError(subscriptionError)) {
        setSubscriptionStatus(null);
      } else {
        const apiError = subscriptionError as ApiError;
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.errors.failedToGetSubscriptionStatus");
        showToast({
          description: errorMessage,
          variant: "destructive",
        });
        setSubscriptionStatus(null);
      }
    }

    const destination = await determineLoginDestination(
      userData,
      onboardingData,
      subscriptionData
    );

    return { shouldNavigateTo: destination };
  };

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = false,
    org?: string | null
  ): Promise<{ shouldNavigateTo: string }> => {
    try {
      const tokenResponse: Token = await authApi.login(email, password, org);
      return await completeSessionAfterTokenResponse(
        tokenResponse,
        rememberMe
      );
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;

      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);

      handleLoginFlowError(apiError);

      throw error;
    }
  };

  const loginWithSocial = async (
    provider: SocialAuthProvider,
    providerToken: string,
    rememberMe: boolean = false,
    org?: string | null
  ): Promise<
    | { outcome: "session"; shouldNavigateTo: string }
    | { outcome: "linking_required"; linking: SocialLinkingHint }
  > => {
    try {
      const result = await authApi.socialLogin(provider, providerToken, org);

      if (result.kind === "linking_required") {
        return {
          outcome: "linking_required",
          linking: result.linking,
        };
      }

      const { shouldNavigateTo } = await completeSessionAfterTokenResponse(
        result.token,
        rememberMe
      );

      return { outcome: "session", shouldNavigateTo };
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;

      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);

      handleLoginFlowError(apiError);

      throw error;
    }
  };

  const applyTokenResponseAfterSocial = async (
    tokenResponse: SocialSessionToken,
    rememberMe: boolean = false
  ): Promise<{ shouldNavigateTo: string }> => {
    try {
      return await completeSessionAfterTokenResponse(
        tokenResponse,
        rememberMe
      );
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;

      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);

      handleLoginFlowError(apiError);

      throw error;
    }
  };

  const loginWithEntra = async (
    code: string,
    rememberMe: boolean = false
  ): Promise<{ shouldNavigateTo: string }> => {
    try {
      const tokenResponse = await authApi.entraExchange({ code });
      return await completeSessionAfterTokenResponse(
        tokenResponse,
        rememberMe
      );
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;

      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);

      handleLoginFlowError(apiError);

      throw error;
    }
  };

  const refreshAfterOnboardingComplete = async (
    body: OnboardingCompleteRequestBody
  ): Promise<OnboardingCompleteResponse> => {
    try {
      const result = await authApi.completeOnboarding(body);

      const userData: UserProfileResponse = await authApi.getCurrentUser();
      setUser(userData);
      if (userData.user.account_completion_required !== true) {
        setAccountCompletionHint(false);
      }

      try {
        const status = await authApi.getOnboardingStatus();
        setOnboardingStatus(status);
      } catch (onboardingError) {
        console.warn(
          t("common.errors.failedToGetOnboardingStatus"),
          onboardingError
        );
        setOnboardingStatus(null);
      }

      return result;
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;
      if (
        apiError.backendData?.error_key === "ERROR_ACCOUNT_COMPLETION_NOT_REQUIRED"
      ) {
        try {
          const userData: UserProfileResponse = await authApi.getCurrentUser();
          setUser(userData);
          setAccountCompletionHint(false);
        } catch {
          // Keep idempotent success behavior even if refresh call fails.
        }
        return {
          onboarding_completed: true,
          message: apiError.message,
        };
      }
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : apiError.message || t("common.error");

      showToast({
        title: t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    }
  };

  const logout = async () => {
    try {
      // Get both tokens before clearing them
      const accessTokenValue = getAccessToken();
      const refreshTokenValue = getRefreshToken();

      // Skip close-all-sessions when free trial is expired (backend returns 430)
      const isSubscriptionExpired =
        subscriptionStatus?.access_reason === "freemium_expired";

      // Call backend logout to blacklist tokens
      const result = await authApi.logout(
        accessTokenValue,
        refreshTokenValue,
        isSubscriptionExpired
      );

      // Clear frontend state
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);
      clearToken();

      showToast({
        title: t("features.auth.logout.success"),
        description:
          result.message || t("features.auth.logout.successDescription"),
        variant: "default",
      });
    } catch (error) {
      const apiError = error as ApiError;

      // Even if backend logout fails, clear frontend state for security
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);
      clearToken();
      if (apiError.backendData) {
        const errorMessage = translateBackendMessage(apiError.backendData, t);
        showToast({
          title: t("features.auth.logout.error"),
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const signup = async (userData: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    date_of_birth: string;
    terms_accepted: boolean;
    privacy_policy_accepted: boolean;
    language: string;
    theme: string;
  }) => {
    try {
      // Step 1: Register the user
      await authApi.register(userData);

      // Get onboarding status for context (but don't use for routing)
      try {
        // const onboardingData = await authApi.getOnboardingStatus();
        // setOnboardingStatus(onboardingData);
      } catch (onboardingError) {
        console.warn(
          t("common.errors.failedToGetOnboardingStatus"),
          onboardingError
        );
        setOnboardingStatus(null);
      }

      // Get subscription status for context (but don't use for routing)
      try {
        // const response = await subscriptionApi.getStatus();
        // const subscriptionData = response.data as SubscriptionStatusResponse;
        // setSubscriptionStatus(subscriptionData);
      } catch (subscriptionError) {
        const apiError = subscriptionError as ApiError;
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.errors.failedToGetSubscriptionStatus");

        showToast({
          description: errorMessage,
          variant: "destructive",
        });
        setSubscriptionStatus(null);
      }

      // Note: rememberMe preference is stored in localStorage by SignupPage
      // for auto-login after email verification. No additional storage needed here.

      showToast({
        title: t("features.auth.signup.success"),
        description: t("features.auth.signup.successDescription"),
        variant: "success",
      });

      // ALWAYS route new users to verification screen first
      // After verification, they'll go to introduction screens
      return {
        // user: currentUserData,
        shouldNavigateTo: "/auth/verification-sent",
      };
    } catch (error: ApiError | Error | unknown) {
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("features.auth.signup.errorDescription");
      showToast({
        title: t("features.auth.signup.error"),
        description: errorMessage,
        variant: "destructive",
      });
      // Clear any partial auth state
      clearToken();
      setUser(null);
      setOnboardingStatus(null);
      setSubscriptionStatus(null);

      throw error;
    }
  };

  const updateUser = (updates: Partial<UserProfileResponse>) => {
    if (user) {
      // Check if updates is the complete API response (has user property)
      // or if it's already a partial update to the user object
      let newUser;

      if (updates.user) {
        // Complete API response - merge the nested user object
        newUser = {
          ...user,
          ...updates,
          user: { ...user.user, ...updates.user },
        };
      } else {
        // Partial update - treat updates as direct user properties
        newUser = {
          ...user,
          user: { ...user.user, ...updates },
        };
      }

      setUser(newUser);
    }
  };

  const resendVerificationEmail = async (email: string): Promise<void> => {
    try {
      await authApi.resendVerificationEmail(email);
      showToast({
        title: t("features.auth.verification.resend.title"),
        description: t("features.auth.verification.resend.description"),
        variant: "success",
      });
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("features.auth.verification.resend.errorDescription");

      showToast({
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    }
  };

  const verifyEmail = async (token: string): Promise<void> => {
    try {
      await authApi.verifyEmail(token);
      showToast({
        title: t("features.auth.verification.verify.successTitle"),
        description: t("features.auth.verification.verify.successDescription"),
        variant: "success",
      });
    } catch (error: any) {
      const apiError = error as ApiError;
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("features.auth.verification.verify.errorDescription");
      showToast({
        title: t("features.auth.verification.verify.errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    }
  };

  const isAuthenticated = !!user && !!getStoredToken();
  const accessDecision = user?.access ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        accessDecision,
        isAuthenticated,
        isLoading,
        onboardingStatus,
        subscriptionStatus,
        login,
        logout,
        signup,
        updateUser,
        getAccessToken,
        checkOnboardingStatus,
        checkSubscriptionStatus,
        refreshCurrentUserAccess,
        resendVerificationEmail,
        verifyEmail,
        loginWithSocial,
        applyTokenResponseAfterSocial,
        loginWithEntra,
        refreshAfterOnboardingComplete,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
